import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sun, ShieldCheck, Eye } from "lucide-react";
import { SectionHead, Reveal, EASE } from "./motion";
import { scrollToId } from "./SmoothScroll";

export const SHADES = [
  { id: "none", vlt: "No tint", name: "Factory", clear: 1 },
  { id: "70", vlt: "70%", name: "Light", clear: 0.72 },
  { id: "50", vlt: "50%", name: "Medium", clear: 0.5 },
  { id: "35", vlt: "35%", name: "Classic", clear: 0.32 },
  { id: "20", vlt: "20%", name: "Dark", clear: 0.16 },
  { id: "5", vlt: "5%", name: "Limo", clear: 0.03 },
];

export const VEHICLES = [
  { id: "sedan", label: "Sedan", price: "$350", pos: "50% 42%" },
  { id: "suv", label: "SUV 5", price: "$400", sub: "5 seater", pos: "50% 40%" },
  { id: "suv7", label: "SUV 7", price: "$450", sub: "7 seater", pos: "50% 40%" },
  { id: "truck", label: "Pickup", price: "$350", pos: "50% 38%" },
];

const mask = (v) => ({
  WebkitMaskImage: `url(/img/tint-${v.id}-mask.png)`, maskImage: `url(/img/tint-${v.id}-mask.png)`,
  WebkitMaskSize: "cover", maskSize: "cover", WebkitMaskPosition: v.pos, maskPosition: v.pos,
  WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
});
const FIT = "absolute inset-0 h-full w-full object-cover";

const Layer = ({ v, active, shade }) => (
  <motion.div className="absolute inset-0" initial={false} animate={{ opacity: active ? 1 : 0, scale: active ? 1 : 1.04 }}
    transition={{ duration: 0.8, ease: EASE }} aria-hidden={!active}>
    <img src={`/img/tint-${v.id}-limo.jpg`} alt={`Black ${v.label} side profile`} className={FIT} style={{ objectPosition: v.pos }} />
    <motion.img src={`/img/tint-${v.id}-clear.jpg`} alt="" aria-hidden="true" style={{ ...mask(v), objectPosition: v.pos }}
      initial={false} animate={{ opacity: shade.clear }} transition={{ duration: 0.7, ease: EASE }} className={FIT} />
    <div style={mask(v)} className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-[#1bb4f2]/10 pointer-events-none" />
  </motion.div>
);

const VehicleSwitch = ({ vehicle, setVehicle, idp = "d", className = "" }) => (
  <div className={className}>
  <div className="flex bg-black/50 backdrop-blur-md border border-white/15 p-1" data-testid={`tint-vehicle-switch-${idp}`}>
    {VEHICLES.map((v) => (
      <button key={v.id} onClick={() => setVehicle(v)} data-testid={`tint-vehicle-${v.id}-${idp}`}
        className={`relative px-3 md:px-5 h-9 md:h-10 text-xs md:text-sm font-semibold transition-colors ${vehicle.id === v.id ? "text-[#07080B]" : "text-slate-200 hover:text-white"}`}>
        {vehicle.id === v.id && <motion.span layoutId={`veh-pill-${idp}`} className="absolute inset-0 bg-[#1bb4f2]" transition={{ duration: 0.45, ease: EASE }} />}
        <span className="relative">{v.label}</span>
      </button>
    ))}
  </div>
  </div>
);

const MobileBar = ({ vehicle, setVehicle }) => (
  <div className="sm:hidden flex flex-col gap-3 p-3 border-b border-white/10">
    <VehicleSwitch vehicle={vehicle} setVehicle={setVehicle} idp="m" className="[&>div]:grid [&>div]:grid-cols-4" />
    <div className="flex items-baseline justify-between"><p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">Full tint · {vehicle.label}{vehicle.sub ? ` (${vehicle.sub})` : ""}</p><p className="font-cond font-bold text-2xl text-[#6fd6ff] leading-none">{vehicle.price}</p></div>
  </div>
);

const Stage = ({ shade, vehicle, setVehicle }) => (
  <div className="relative aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] w-full overflow-hidden bg-[#0e1118]" data-testid="tint-preview">
    {VEHICLES.map((v) => <Layer key={v.id} v={v} active={v.id === vehicle.id} shade={shade} />)}
    <div className="absolute top-4 left-4 md:top-6 md:left-6 pointer-events-none">
      <motion.p key={shade.id} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: EASE }}
        className="font-display font-black text-3xl sm:text-4xl md:text-7xl leading-none chrome-text" data-testid="tint-current-vlt">{shade.vlt}</motion.p>
      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-[#6fd6ff]">{shade.id === "none" ? "Untinted glass" : `${shade.name} · light let through`}</p>
    </div>
    <VehicleSwitch vehicle={vehicle} setVehicle={setVehicle} className="hidden sm:block absolute top-4 right-4 md:top-6 md:right-6" />
    <motion.div key={vehicle.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
      className="hidden sm:block absolute bottom-4 right-4 md:bottom-6 md:right-6 bg-black/50 backdrop-blur-md border border-white/15 px-4 py-2 text-right" data-testid="tint-vehicle-price">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">Full tint · {vehicle.id === "truck" ? "Pickup" : vehicle.id === "sedan" ? "Sedan" : "SUV"}{vehicle.sub ? ` (${vehicle.sub})` : ""}</p>
      <p className="font-cond font-bold text-2xl md:text-3xl text-[#6fd6ff] leading-none mt-1">{vehicle.price}</p>
    </motion.div>
  </div>
);

export const TintPicker = ({ onPick }) => {
  const [shade, setShade] = useState(SHADES[3]);
  const [vehicle, setVehicle] = useState(VEHICLES[0]);
  return (
    <section id="tint-picker" data-testid="tint-picker-section" className="relative mx-auto max-w-[1440px] px-5 md:px-10 py-24 md:py-36">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12 md:mb-16">
        <SectionHead eyebrow="Tint shade picker" lines={["Pick your", "darkness."]}>
          Tap through nano-ceramic shades on a sedan, SUV (5 or 7 seat) or pickup to see how dark you want to go. Lower % means darker glass.
        </SectionHead>
        <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
          <li className="flex items-center gap-2"><Sun className="h-4 w-4 text-[#1bb4f2]" />Heat rejection</li>
          <li className="flex items-center gap-2"><Eye className="h-4 w-4 text-[#1bb4f2]" />Up to 99% UV blocked</li>
          <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#1bb4f2]" />Lifetime warranty</li>
        </ul>
      </div>
      <Reveal>
        <div className="border border-white/10 bg-[#0b0d12]">
          <MobileBar vehicle={vehicle} setVehicle={setVehicle} />
          <Stage shade={shade} vehicle={vehicle} setVehicle={setVehicle} />
          <div className="grid grid-cols-3 sm:grid-cols-6 border-t border-white/10" data-testid="tint-shade-options">
            {SHADES.map((s) => (
              <button key={s.id} onClick={() => setShade(s)} data-testid={`tint-shade-${s.id}`}
                className={`relative p-4 md:p-5 text-left border-r border-b sm:border-b-0 border-white/10 transition-colors ${shade.id === s.id ? "bg-[#1bb4f2] text-[#07080B]" : "hover:bg-white/5"}`}>
                <span className="block h-2 w-full mb-3 border border-white/20" style={{ background: `hsl(200 12% ${8 + s.clear * 78}%)` }} />
                <span className="block font-cond font-bold text-2xl leading-none">{s.vlt}</span>
                <span className={`block text-xs mt-1 ${shade.id === s.id ? "text-[#07080B]" : "text-slate-400"}`}>{s.name}</span>
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 md:p-6 border-t border-white/10">
            <p className="text-xs text-slate-400 max-w-md">Preview is a simulation. We confirm film, shade and window coverage with you when quoting.</p>
            <button onClick={() => { onPick(`${shade.vlt} on ${vehicle.label}`); scrollToId("quote"); }} data-testid="tint-request-quote"
              className="group inline-flex items-center justify-center gap-3 h-14 px-7 bg-[#1bb4f2] text-[#07080B] font-semibold hover:bg-white transition-colors">
              Quote me {shade.id === "none" ? "for tint" : `${shade.vlt} tint`} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </Reveal>
    </section>
  );
};
